import {
  consumePersistentKeyboardFocusHandoff,
  persistentKeyboardFocus,
} from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { animationFrames } from "@-/dev.improve.haniverse.lib.scheduler";
import { useEffect } from "react";

// After rating the previous card, the persistent-keyboard session remains active while the old
// input unmounts. UIKit can still animate the OS keyboard closed during the route replacement, so
// the next card must restore from the hand-off token rather than from transient OS visibility.
const REFOCUS_FRAME_ATTEMPTS = 30;

/**
 * Re-focus the new drill card's answer input when the card becomes active, but ONLY when the
 * previous card explicitly handed focus forward (reveal / rating both call
 * `persistentKeyboardPreserveFocus()`, which arms a one-shot token).
 *
 * Gating on the token rather than on "a keyboard session is active" is what keeps the FIRST card of
 * a review session unfocused: arriving from the learn summary or a lesson-level boundary never arms
 * it, so the user must tap the answer input to summon the keyboard. An explicit dismiss also clears
 * the token, so a user who collapsed the keyboard mid-session stays collapsed.
 *
 * The native `focus()` binds first-responder to the most recently mounted `PersistentKeyboardView`,
 * so this must run after that view has attached to the window. The React focus effect can fire a
 * frame ahead of the native attach, so we retry across a few frames until the cursor lands.
 */
export function useDrillAnswerInputAutoFocus() {
  const isFocused = useIsFocused();

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    // Consumed here (not read) so a single hand-off can only revive the keyboard for the one card
    // it was armed for — otherwise a later fresh entry would inherit a stale token.
    if (!consumePersistentKeyboardFocusHandoff()) {
      return;
    }
    let isCancelled = false;
    const refocus = async () => {
      let attempts = 0;
      for await (const _frame of animationFrames()) {
        if (isCancelled) {
          return;
        }
        persistentKeyboardFocus();
        attempts += 1;
        if (attempts >= REFOCUS_FRAME_ATTEMPTS) {
          return;
        }
      }
    };
    void refocus();
    return () => {
      isCancelled = true;
    };
  }, [isFocused]);
}
